/**
 * FractionFieldConfigPanel Component
 *
 * Configuration panel for Fraction field type following the existing UI-Model pattern.
 * Uses consistent 4-section layout: Label (disabled), Style Type, Show Title section, Field exclusions.
 * Uses Design System components for all UI elements.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FractionFieldConfigPanelProps } from '../Fields/types';
import { FRACTION_DEFAULT_CONFIG, FractionFieldConfig } from '../../config/fieldDefaults';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Radio from 'Components/DesignSystem/Radio/Radio';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';

const DEBOUNCE_DELAY = 500;

export const FractionFieldConfigPanel = ({
  config,
  onChange,
}: FractionFieldConfigPanelProps) => {
  const [localConfig, setLocalConfig] = useState<FractionFieldConfig>(() => {
    const defaults = { label: '', displayName: '', shortDisplayName: '', ...FRACTION_DEFAULT_CONFIG };
    return { ...defaults, ...config };
  });

  useEffect(() => {
    const defaults = { label: '', displayName: '', shortDisplayName: '', ...FRACTION_DEFAULT_CONFIG };
    setLocalConfig({ ...defaults, ...config });
  }, [config]);

  const handleConfigChange = useCallback(
    <K extends keyof FractionFieldConfig>(key: K, value: FractionFieldConfig[K]) => {
      const newConfig = { ...localConfig, [key]: value };
      setLocalConfig(newConfig);
      onChange(newConfig);
    },
    [localConfig, onChange]
  );

  const displayNameInput = useDebouncedInput(
    localConfig.displayName,
    (value) => handleConfigChange('displayName', value),
    DEBOUNCE_DELAY
  );

  const shortDisplayNameInput = useDebouncedInput(
    localConfig.shortDisplayName || '',
    (value) => handleConfigChange('shortDisplayName', value),
    DEBOUNCE_DELAY
  );

  return (
    <div className="tw-p-3">
      {/* 1. Label Section - Read-only (matches existing UI-Model pattern) */}
      <div className="tw-mb-6">
        <TextField
          label="Label"
          labelSize="sm"
          value={localConfig.label}
          disabled
          placeholder="Field name"
        />
      </div>

      {/* 2. Style Type Section (matches existing UI-Model pattern) */}
      <div className="tw-mb-6">
        <FieldTitle label="Style Type" size="sm" className="tw-mb-2" />
        <Typography variant="body-primary" className="tw-text-ink">
          Fraction Field
        </Typography>
      </div>

      {/* 3. Show Title Section with conditional Display Name & Short Display Name */}
      <div className="tw-mb-4">
        <Checkbox
          label="Show Title"
          checked={localConfig.showTitle}
          onChange={(checked) => handleConfigChange('showTitle', checked)}
        />

        {/* Display Name - shown when Show Title is checked */}
        {localConfig.showTitle && (
          <div className="tw-mt-3 tw-ml-8">
            <TextField
              label="Display Name"
              labelSize="sm"
              value={displayNameInput.localValue}
              onChange={displayNameInput.handleChange}
              placeholder="Enter display name"
            />
          </div>
        )}

        {/* Short Display Name - shown when Show Title is checked */}
        {localConfig.showTitle && (
          <div className="tw-mt-3 tw-ml-8">
            <TextField
              label="Short Display Name (Optional)"
              labelSize="sm"
              value={shortDisplayNameInput.localValue}
              onChange={shortDisplayNameInput.handleChange}
              placeholder="Enter Short Display Name"
              infoMessage="A concise name displayed in the build/configuration view instead of the full Display Name. Example: Use 'Finish' instead of 'Would you like to select a finish for your product?' If left empty, the Display Name will be used."
            />
          </div>
        )}
      </div>

      {/* 4. Field Exclusions Section (matches existing UI-Model pattern with radio buttons) */}
      <div className="tw-mb-6">
        <FieldTitle label="Field exclusions" size="sm" className="tw-mb-2" />
        <div className="tw-flex tw-items-center tw-gap-8">
          <Radio
            label="Hide"
            value="hide"
            checked={localConfig.exclusionType === 'hide'}
            onChange={() => handleConfigChange('exclusionType', 'hide')}
            name="fractionFieldExclusion"
          />
          <Radio
            label="Disable"
            value="disable"
            checked={localConfig.exclusionType === 'disable'}
            onChange={() => handleConfigChange('exclusionType', 'disable')}
            name="fractionFieldExclusion"
          />
        </div>
      </div>
    </div>
  );
};

export default FractionFieldConfigPanel;
